import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MapPin, Plus, Edit, Trash2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { GpsLicenseGate } from '@/components/gps/GpsLicenseGate';
import GeofenceMap from '@/components/gps/GeofenceMap';
import { toast } from 'sonner';

interface Geofence {
  id: string;
  name: string;
  type: 'circle' | 'polygon';
  center_lat?: number;
  center_lng?: number;
  radius_m?: number;
  polygon_geojson?: any;
  active: boolean;
  created_at: string;
}

export default function GeofencesPage() {
  const { user } = useAuth();
  const companyId = user?.user_metadata?.company_id;
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadGeofences();
  }, []);

  const loadGeofences = async () => {
    try {
      // Mock data for now
      const mockGeofences: Geofence[] = [
        {
          id: '1',
          name: 'Depósito Central',
          type: 'circle',
          center_lat: -23.5505,
          center_lng: -46.6333,
          radius_m: 200,
          active: true,
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          name: 'Zona de Entrega Norte',
          type: 'polygon',
          polygon_geojson: {
            type: 'Polygon',
            coordinates: [[
              [-46.6200, -23.5400],
              [-46.6100, -23.5400],
              [-46.6100, -23.5300],
              [-46.6200, -23.5300],
              [-46.6200, -23.5400]
            ]]
          },
          active: true,
          created_at: new Date().toISOString()
        }
      ];
      
      setGeofences(mockGeofences);
    } catch (error) {
      toast.error('Erro ao carregar geocercas');
    } finally {
      setIsLoading(false);
    }
  };

  const validateGeofenceName = (name: string, excludeId?: string): boolean => {
    const exists = geofences.some(
      g => g.name.toLowerCase() === name.toLowerCase() && g.id !== excludeId
    );
    
    if (exists) {
      toast.error('Já existe uma geocerca com este nome');
      return false;
    }
    
    return true;
  };

  const handleCreateGeofence = async (geofenceData: Omit<Geofence, 'id'>) => {
    try {
      // Validate unique name
      if (!validateGeofenceName(geofenceData.name)) {
        return;
      }

      // Simulate API call
      const newGeofence: Geofence = {
        ...geofenceData,
        id: Date.now().toString(),
        created_at: new Date().toISOString()
      };
      
      setGeofences(prev => [...prev, newGeofence]);
      toast.success('Geocerca criada com sucesso!');
    } catch (error) {
      toast.error('Erro ao criar geocerca');
    }
  };

  const handleUpdateGeofence = async (id: string, updateData: Partial<Geofence>) => {
    try {
      // Validate unique name if name is being updated
      if (updateData.name && !validateGeofenceName(updateData.name, id)) {
        return;
      }

      setGeofences(prev => prev.map(g => 
        g.id === id ? { ...g, ...updateData } : g
      ));
      toast.success('Geocerca atualizada com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar geocerca');
    }
  };

  const handleDeleteGeofence = async (id: string) => {
    try {
      setGeofences(prev => prev.filter(g => g.id !== id));
      toast.success('Geocerca removida com sucesso!');
    } catch (error) {
      toast.error('Erro ao remover geocerca');
    }
  };

  const toggleGeofenceStatus = (id: string) => {
    const geofence = geofences.find(g => g.id === id);
    if (geofence) {
      handleUpdateGeofence(id, { active: !geofence.active });
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-64">Carregando geocercas...</div>;
  }

  return (
    <GpsLicenseGate>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <MapPin className="h-8 w-8 text-primary" />
              Geocercas
            </h1>
            <p className="text-muted-foreground">
              Gerencie áreas geográficas para monitoramento
            </p>
          </div>
        </div>

        {/* Info Alert */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Configure áreas geográficas para monitoramento de entrada e saída de veículos.
          </AlertDescription>
        </Alert>

        {/* Geofences List */}
        <Card>
          <CardHeader>
            <CardTitle>Geocercas Cadastradas ({geofences.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {geofences.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma geocerca cadastrada
              </div>
            ) : (
              <div className="space-y-3">
                {geofences.map((geofence) => (
                  <div
                    key={geofence.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{geofence.name}</h3>
                        <Badge variant={geofence.active ? "default" : "secondary"}>
                          {geofence.type === 'circle' ? 'Círculo' : 'Polígono'}
                        </Badge>
                        <Badge variant={geofence.active ? "default" : "secondary"}>
                          {geofence.active ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {geofence.type === 'circle' 
                          ? `Raio: ${geofence.radius_m}m`
                          : 'Área poligonal personalizada'
                        }
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleGeofenceStatus(geofence.id)}
                      >
                        {geofence.active ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteGeofence(geofence.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map */}
        <GeofenceMap
          geofences={geofences}
          onGeofenceCreate={handleCreateGeofence}
          onGeofenceUpdate={handleUpdateGeofence}
          onGeofenceDelete={handleDeleteGeofence}
        />
      </div>
    </GpsLicenseGate>
  );
}
